/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/build/QaModePanes.tsx
 * FILE_ID: JOBHUB-QA-MODE-PANES-001
 * PURPOSE: Shared Areas-adjacent split: checklist rail plus a mode body.
 */
import type { ReactNode } from 'react'
import { ResizeHandle, useIsLgUp, usePersistentBool, usePersistentSize } from '@quotech/ui'
import type { QaChecklistGroup } from '../../../qa/qaApiHelpers'
import { QA_BUILD_DETAIL_SPLIT_CLASS, QA_MODE_BODY_SCROLL_CLASS } from '../qaWorkspaceLayout'
import { QaChecklistRail } from './QaChecklistRail'

export function QaModePanes({
  groups,
  selectedId,
  onSelect,
  onRename,
  onAdd,
  adding = false,
  disabled = false,
  selectedIds,
  onToggleSelected,
  startRenameId,
  onRenameStarted,
  onOpenMenu,
  reorderFor,
  areaId,
  bodyClassName = QA_MODE_BODY_SCROLL_CLASS,
  hideRail = false,
  checklistsCollapsed: checklistsCollapsedProp,
  onToggleChecklists,
  children,
}: {
  groups: QaChecklistGroup[]
  selectedId: string | null
  onSelect: (id: string | null) => void
  onRename?: (checklistId: string, title: string) => void
  onAdd?: () => void
  adding?: boolean
  disabled?: boolean
  selectedIds?: ReadonlySet<string>
  onToggleSelected?: (checklistId: string) => void
  startRenameId?: string | null
  onRenameStarted?: () => void
  onOpenMenu?: (checklistId: string, event: { clientX: number; clientY: number }) => void
  reorderFor?: (checklistId: string) => {
    canMoveUp: boolean
    canMoveDown: boolean
    busy?: boolean
    onMoveUp: () => void
    onMoveDown: () => void
  } | undefined
  areaId?: string
  /** Build passes QA_BUILD_PANE_CLASS; the table then owns vertical scroll. */
  bodyClassName?: string
  hideRail?: boolean
  checklistsCollapsed?: boolean
  onToggleChecklists?: () => void
  children: ReactNode
}) {
  const [width, setWidth] = usePersistentSize('qa.checklists.width', 220)
  // Reused from: packages/ui/src/components/panelResize.tsx (usePersistentBool)
  const [storedCollapsed, setStoredCollapsed] = usePersistentBool('qa.checklists.collapsed', false)
  const checklistsCollapsed = checklistsCollapsedProp ?? storedCollapsed
  const toggleChecklists = onToggleChecklists ?? (() => setStoredCollapsed(!storedCollapsed))
  const isLgUp = useIsLgUp()
  // Chip strip stays below lg (QA_BUILD_DETAIL_SPLIT_CLASS). Collapse is lg only.
  const railCollapsed = isLgUp && checklistsCollapsed
  if (hideRail || railCollapsed) return <div className={bodyClassName}>{children}</div>
  return (
    <div
      className={QA_BUILD_DETAIL_SPLIT_CLASS}
      style={{ gridTemplateColumns: railCollapsed ? 'auto 0px 1fr' : `${width}px 4px 1fr` }}
    >
      <QaChecklistRail
        groups={groups}
        selectedId={selectedId}
        onSelect={onSelect}
        onRename={onRename}
        onAdd={onAdd}
        adding={adding}
        disabled={disabled}
        selectedIds={selectedIds}
        onToggleSelected={onToggleSelected}
        startRenameId={startRenameId}
        onRenameStarted={onRenameStarted}
        onOpenMenu={onOpenMenu}
        reorderFor={reorderFor}
        areaId={areaId}
        collapsed={railCollapsed}
        onToggleCollapsed={toggleChecklists}
      />
      {railCollapsed ? null : (
        <ResizeHandle
          axis="x"
          sign={1}
          value={width}
          min={140}
          max={420}
          onChange={setWidth}
          resetTo={220}
          ariaLabel="Resize checklists column"
          className="hidden lg:block"
        />
      )}
      <div className={bodyClassName}>{children}</div>
    </div>
  )
}
